import { Check, Copy } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { type Separator, slugify } from "../lib/slug";

const SAMPLE = "Hello World! This is Toolverse — the multi-tool platform.";

const selectClass =
  "h-10 w-full rounded-md border border-border bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

export function SlugGenerator() {
  const uid = useId();
  const [input, setInput] = useState(SAMPLE);
  const [separator, setSeparator] = useState<Separator>("-");
  const [lowercase, setLowercase] = useState(true);
  const [maxLength, setMaxLength] = useState(0);
  const [copied, setCopied] = useState(false);

  const slug = useMemo(
    () =>
      slugify(input, {
        separator,
        lowercase,
        maxLength: maxLength > 0 ? maxLength : undefined,
      }),
    [input, separator, lowercase, maxLength],
  );

  const copy = async () => {
    if (!slug) return;
    try {
      await navigator.clipboard.writeText(slug);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable — ignore
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4 rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
        <div className="space-y-1.5">
          <Label htmlFor={`${uid}-in`}>{m.sg_input_label()}</Label>
          <textarea
            id={`${uid}-in`}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={m.sg_placeholder()}
            className="min-h-[6rem] w-full resize-y rounded-lg border border-border bg-background p-4 text-sm"
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor={`${uid}-sep`}>{m.sg_separator()}</Label>
            <select
              id={`${uid}-sep`}
              className={selectClass}
              value={separator}
              onChange={(e) => setSeparator(e.target.value as Separator)}
            >
              <option value="-">{m.sg_sep_hyphen()}</option>
              <option value="_">{m.sg_sep_underscore()}</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${uid}-max`}>{m.sg_maxlength()}</Label>
            <Input
              id={`${uid}-max`}
              type="number"
              min={0}
              value={maxLength || ""}
              onChange={(e) =>
                setMaxLength(Math.max(0, Number(e.target.value) || 0))
              }
              placeholder="0"
            />
          </div>
          <div className="flex items-end">
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={lowercase}
                onChange={(e) => setLowercase(e.target.checked)}
                className="h-4 w-4 rounded border-border accent-primary"
              />
              {m.sg_lowercase()}
            </label>
          </div>
        </div>
      </div>

      <div className="space-y-3 rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-bold tracking-tight">
            {m.sg_output_label()}
          </h2>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={copy}
            disabled={!slug}
            className="gap-2"
          >
            {copied ? (
              <Check className="h-4 w-4 text-emerald-500" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
            {copied ? m.sg_copied() : m.sg_copy()}
          </Button>
        </div>
        <Input
          readOnly
          data-testid="sg-output"
          aria-label={m.sg_output_label()}
          value={slug}
          className="font-mono text-sm"
        />
      </div>
    </div>
  );
}
